/**
 * SPEC 2026-10-26 Story 1 — Settings → Operations → "Required vaccinations".
 *
 * The owner's own list of the vaccines their shop requires, which services
 * each applies to, and how long a lapsed shot is tolerated. It sits on the
 * Operations card directly under the off / warn / block gate, because the
 * gate does nothing without it (AC 1.6), and it loads on its own: a slow or
 * missing list never holds the rest of the card hostage, and the card never
 * holds the list.
 *
 * Server contract: GET/POST/PUT/DELETE /api/v2/admin/tenant/vaccination-
 * requirements (+ /preview). Errors arrive as `{ code, error }` with `error`
 * already the owner's sentence, so it is shown as-is (AC 1.5's
 * "Rabies is already required for all services. Edit it instead.").
 *
 * Who can do what (AC 1.8, D1): `canEdit` is the owner
 * (business-config:write:all) OR the manager
 * (business-config.vaccinations:write:all). Everyone else who can open
 * Settings sees the list read-only, with no controls.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: one line per vaccine ("Rabies · All services ·
 *    7-day grace") and the coverage count comes from the server's run of the
 *    gate's own rule — never a second copy computed here.
 *  - #2 The next action is obvious: the empty state teaches and offers ONE
 *    primary action; an "Other" name that is really a listed vaccine says so
 *    and offers the one-tap fix.
 *  - #5 Never lie about data: an older server reads "Available after the next
 *    server update" (AC 8.2), not an error and not an empty list; the last
 *    change names who and when (AC 1.9).
 *  - #7 Built for hands: every control is ≥ var(--ferro-tap); the editor is
 *    inline (no modal-on-modal) and single-column at 375 px.
 *  - Five states: loading (skeleton, first load only — never after data) ·
 *    error (+ Try again) · empty (teaching copy + primary action) · ready ·
 *    unavailable (old server). Stale does not apply: it reads fresh on every
 *    open and every write answers with the whole list.
 *
 * Counter Test: Ted adds Rabies for all services with a 7-day grace, sees
 * "312 of 340 dogs on file have a current Rabies record" before he saves,
 * and the next online booking is judged by it. He never asks Johnny.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Alert, Button, Checkbox, Input, Select, Skeleton } from '@ferro/ui';
import { isRouteNotMapped, type ApiResponse } from '../../lib/api.js';
import {
  adminVaccinationRequirementsApi,
  type CreateVaccinationRequirementInput,
  type UpdateVaccinationRequirementInput,
  type VaccinationAppliesToInput,
  type VaccinationRequirementView,
  type VaccinationRequirementsView,
  type VaccineCoverage,
  type VaccineNamePreview,
} from '../../lib/vaccination-requirements-api.js';
import { whenLabel } from './last-change-label.js';
import './VaccinationRequirementsSection.css';

export interface VaccinationRequirementsSectionProps {
  apiUrl: string;
  /** Add, edit and remove requirements: the owner or a manager (D1). */
  canEdit: boolean;
  /**
   * How many vaccines are required — null while unknown (loading, failed, or
   * an older server). The card uses it for "Block has no effect until you
   * add a required vaccine." (AC 1.6), so an unknown never claims "none".
   */
  onRequirementCountChange?: (count: number | null) => void;
}

type Status = 'loading' | 'error' | 'unavailable' | 'ready';

/** The select value for "Other". Never a catalog key (those are words like `rabies`). */
export const OTHER_VACCINE = '__other__';

type EditorMode = { kind: 'add' } | { kind: 'edit'; vaccineId: string };

export interface RequirementDraft {
  /** '' (nothing chosen yet), a catalog key, or OTHER_VACCINE. Ignored when editing. */
  choice: string;
  otherName: string;
  appliesKind: 'all' | 'services';
  serviceIds: string[];
  /** Days, as typed. */
  grace: string;
  note: string;
}

const EMPTY_DRAFT: RequirementDraft = {
  choice: '',
  otherName: '',
  appliesKind: 'all',
  serviceIds: [],
  grace: '0',
  note: '',
};

const PREVIEW_DEBOUNCE_MS = 400;
/** How many accepted spellings show before "and N more". */
const ACCEPTS_SHOWN = 4;

// ── Pure helpers (exported for the unit tests) ─────────────────────────────

/** "All services" or "Boarding, Daycare". */
export function appliesToLabel(req: Pick<VaccinationRequirementView, 'appliesTo'>): string {
  if (req.appliesTo.kind === 'all') return 'All services';
  const names = req.appliesTo.services.map((s) => s.name);
  return names.length > 0 ? names.join(', ') : 'No services';
}

/** "Rabies · All services · 7-day grace" — the grace part only when there is one. */
export function requirementSummary(
  req: Pick<VaccinationRequirementView, 'name' | 'appliesTo' | 'gracePeriodDays'>,
): string {
  const parts = [req.name, appliesToLabel(req)];
  if (req.gracePeriodDays > 0) parts.push(`${req.gracePeriodDays}-day grace`);
  return parts.join(' · ');
}

/** "312 of 340 dogs on file have a current Rabies record". */
export function coverageSentence(name: string, coverage: VaccineCoverage): string {
  if (coverage.onFile === 0) return 'No dogs on file yet, so there is nothing to count.';
  const dogs = coverage.onFile === 1 ? 'dog' : 'dogs';
  const verb = coverage.current === 1 ? 'has' : 'have';
  return `${coverage.current} of ${coverage.onFile} ${dogs} on file ${verb} a current ${name} record`;
}

/** Short words that are words, not acronyms, in a vaccine name. */
const SHORT_WORDS = new Set(['dog', 'flu', 'lyme', 'lepto', 'parvo', 'cough', 'shot']);

/**
 * A comparison key as a person reads it: "dhpp" → "DHPP", "da2pp cpv" →
 * "DA2PP CPV", "kennel cough" → "Kennel cough", "h3n2" → "H3N2".
 */
export function displayVaccineKey(key: string): string {
  const tokens = key.trim().split(/\s+/).filter((t) => t.length > 0);
  return tokens
    .map((t, i) => {
      if (/\d/.test(t) || (t.length <= 5 && !SHORT_WORDS.has(t))) return t.toUpperCase();
      return i === 0 ? t.charAt(0).toUpperCase() + t.slice(1) : t;
    })
    .join(' ');
}

/**
 * The record names that count, as a person reads them, de-duplicated. Empty
 * when the only spelling is the vaccine's own name (Rabies counts "Rabies";
 * saying so is noise).
 */
export function acceptedNames(name: string, accepts: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of accepts) {
    const label = displayVaccineKey(key);
    const k = label.toLowerCase();
    if (label.length === 0 || seen.has(k)) continue;
    seen.add(k);
    out.push(label);
  }
  if (out.length === 1 && out[0]!.toLowerCase() === name.trim().toLowerCase()) return [];
  return out;
}

export type DraftCheck =
  | { ok: true; create: CreateVaccinationRequirementInput; update: UpdateVaccinationRequirementInput }
  | { ok: false; error: string };

/** The draft as a request body, or the sentence that says what to fix. Never sends a guess. */
export function checkDraft(
  draft: RequirementDraft,
  mode: EditorMode,
  limits: VaccinationRequirementsView['limits'],
): DraftCheck {
  let vaccine: CreateVaccinationRequirementInput['vaccine'] | null = null;
  if (mode.kind === 'add') {
    if (draft.choice === '') return { ok: false, error: 'Choose a vaccine from the list, or Other.' };
    if (draft.choice === OTHER_VACCINE) {
      const name = draft.otherName.trim();
      if (name.length === 0) return { ok: false, error: 'Give the vaccine a name.' };
      if (name.length > limits.maxNameLength) {
        return { ok: false, error: `Keep the name under ${limits.maxNameLength} characters.` };
      }
      vaccine = { kind: 'other', name };
    } else {
      vaccine = { kind: 'catalog', key: draft.choice };
    }
  }
  let appliesTo: VaccinationAppliesToInput = { kind: 'all' };
  if (draft.appliesKind === 'services') {
    if (draft.serviceIds.length === 0) {
      return { ok: false, error: 'Choose at least one service, or All services.' };
    }
    appliesTo = { kind: 'services', serviceTypeIds: [...draft.serviceIds] };
  }
  const graceText = draft.grace.trim();
  const grace = graceText === '' ? Number.NaN : Number(graceText);
  if (!Number.isInteger(grace) || grace < 0 || grace > limits.maxGracePeriodDays) {
    return { ok: false, error: `Enter a number of days from 0 to ${limits.maxGracePeriodDays}.` };
  }
  const note = draft.note.trim();
  if (note.length > limits.maxNoteLength) {
    return { ok: false, error: `Keep the note under ${limits.maxNoteLength} characters.` };
  }
  const update: UpdateVaccinationRequirementInput = {
    appliesTo,
    gracePeriodDays: grace,
    description: note.length === 0 ? null : note,
  };
  return {
    ok: true,
    update,
    create: { vaccine: vaccine ?? { kind: 'catalog', key: '' }, ...update },
  };
}

/** The sentence an owner reads for a failed write. The server's own words win. */
export function writeErrorMessage(res: ApiResponse<unknown>): string {
  if (isRouteNotMapped(res)) return 'Available after the next server update.';
  if (res.error === 'Validation failed' && Array.isArray(res.details)) {
    const first = (res.details as Array<{ message?: unknown }>).find((d) => typeof d?.message === 'string');
    if (first && typeof first.message === 'string') return first.message;
  }
  return res.error ?? "We couldn't save that. Try again in a moment.";
}

function draftFrom(req: VaccinationRequirementView): RequirementDraft {
  return {
    choice: req.vaccineId,
    otherName: '',
    appliesKind: req.appliesTo.kind,
    serviceIds: req.appliesTo.kind === 'services' ? req.appliesTo.services.map((s) => s.id) : [],
    grace: String(req.gracePeriodDays),
    note: req.description ?? '',
  };
}

/** Enter in a text field saves THIS editor — never the Operations form it sits inside. */
function keepEnterLocal(e: KeyboardEvent<HTMLDivElement>, onEnter: () => void): void {
  if (e.key !== 'Enter') return;
  const target = e.target as HTMLElement;
  if (target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'checkbox' && (target as HTMLInputElement).type !== 'radio') {
    e.preventDefault();
    onEnter();
  }
}

// ── Pieces ─────────────────────────────────────────────────────────────────

function AcceptsLine({ req }: { req: VaccinationRequirementView }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (req.kind === 'custom') {
    return (
      <p className="ferro-vax-req__accepts" data-testid={`vax-req-accepts-${req.vaccineId}`}>
        Counts only records named {req.name}.
      </p>
    );
  }
  const names = acceptedNames(req.name, req.accepts);
  if (names.length === 0) return null;
  const shown = open ? names : names.slice(0, ACCEPTS_SHOWN);
  const hidden = names.length - shown.length;
  return (
    <p className="ferro-vax-req__accepts" data-testid={`vax-req-accepts-${req.vaccineId}`}>
      Counts: {shown.join(', ')}
      {hidden > 0 ? (
        <>
          {' '}
          <button
            type="button"
            className="ferro-vax-req__more"
            onClick={() => setOpen(true)}
            aria-label={`Show all ${names.length} names that count for ${req.name}`}
          >
            and {hidden} more
          </button>
        </>
      ) : null}
    </p>
  );
}

// ── The section ────────────────────────────────────────────────────────────

export function VaccinationRequirementsSection({
  apiUrl,
  canEdit,
  onRequirementCountChange,
}: VaccinationRequirementsSectionProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [view, setView] = useState<VaccinationRequirementsView | null>(null);
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [draft, setDraft] = useState<RequirementDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<{ id: string; message: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ name: string; data: VaccineNamePreview } | null>(null);
  const previewSeq = useRef(0);
  const firstFieldRef = useRef<HTMLSelectElement | HTMLInputElement | null>(null);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      if (!opts.quiet) setStatus('loading');
      const res = await adminVaccinationRequirementsApi.get(apiUrl);
      if (isRouteNotMapped(res)) {
        setStatus('unavailable');
        return;
      }
      if (res.error || !res.data) {
        // A quiet refresh that fails keeps what is on screen (it came from
        // the server moments ago); only a first load shows the error state.
        if (!opts.quiet) setStatus('error');
        return;
      }
      setView(res.data);
      setStatus('ready');
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const count = status === 'ready' && view ? view.requirements.length : null;
  useEffect(() => {
    onRequirementCountChange?.(count);
  }, [count, onRequirementCountChange]);

  // "Other": ask the server whether the typed name is a listed vaccine, and
  // how many dogs it would cover. Debounced; a stale answer is dropped.
  const otherName = editor?.kind === 'add' && draft.choice === OTHER_VACCINE ? draft.otherName.trim() : '';
  useEffect(() => {
    if (otherName.length === 0) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    const timer = setTimeout(() => {
      void adminVaccinationRequirementsApi.preview(apiUrl, otherName).then((res) => {
        if (seq !== previewSeq.current) return;
        setPreview(res.data ? { name: otherName, data: res.data } : null);
      });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [apiUrl, otherName]);

  useEffect(() => {
    if (editor) firstFieldRef.current?.focus();
  }, [editor]);

  const openAdd = (): void => {
    setEditor({ kind: 'add' });
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setNotice(null);
    setConfirmRemoveId(null);
    setPreview(null);
  };

  const openEdit = (req: VaccinationRequirementView): void => {
    setEditor({ kind: 'edit', vaccineId: req.vaccineId });
    setDraft(draftFrom(req));
    setFormError(null);
    setNotice(null);
    setConfirmRemoveId(null);
  };

  const closeEditor = (): void => {
    setEditor(null);
    setFormError(null);
    setPreview(null);
  };

  const applyWrite = (next: VaccinationRequirementsView): void => {
    setView(next);
    setStatus('ready');
  };

  const save = async (): Promise<void> => {
    if (!view || !editor || saving) return;
    setFormError(null);
    const checked = checkDraft(draft, editor, view.limits);
    if (!checked.ok) {
      setFormError(checked.error);
      return;
    }
    setSaving(true);
    const res =
      editor.kind === 'add'
        ? await adminVaccinationRequirementsApi.create(apiUrl, checked.create)
        : await adminVaccinationRequirementsApi.update(apiUrl, editor.vaccineId, checked.update);
    setSaving(false);
    if (res.error || !res.data) {
      setFormError(writeErrorMessage(res));
      // Someone else changed the list meanwhile: show the list as it is now,
      // behind the sentence, without a skeleton.
      if (res.code === 'already_required' || res.code === 'requirement_not_found') void load({ quiet: true });
      return;
    }
    applyWrite(res.data);
    const chosen = checked.create.vaccine;
    const saved =
      editor.kind === 'add'
        ? res.data.requirements.find((r) =>
            chosen.kind === 'catalog'
              ? r.vaccineId === chosen.key
              : r.kind === 'custom' && r.name.toLowerCase() === chosen.name.toLowerCase(),
          )
        : res.data.requirements.find((r) => r.vaccineId === editor.vaccineId);
    setNotice(
      saved
        ? `Saved. ${saved.name} is required for ${saved.appliesTo.kind === 'all' ? 'all services' : appliesToLabel(saved)}.`
        : 'Saved.',
    );
    closeEditor();
  };

  const remove = async (req: VaccinationRequirementView): Promise<void> => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(null);
    const res = await adminVaccinationRequirementsApi.remove(apiUrl, req.vaccineId);
    setRemoving(false);
    if (res.error || !res.data) {
      setRemoveError({ id: req.vaccineId, message: writeErrorMessage(res) });
      if (res.code === 'requirement_not_found') void load({ quiet: true });
      return;
    }
    applyWrite(res.data);
    setConfirmRemoveId(null);
    setNotice(`Removed ${req.name}. Customers will no longer be asked for it.`);
  };

  const catalogOptions = useMemo(() => {
    if (!view) return [];
    return [
      ...view.catalog.map((c) => ({
        value: c.key,
        label: c.alreadyRequired ? `${c.name} (already required)` : c.name,
        disabled: c.alreadyRequired,
      })),
      { value: OTHER_VACCINE, label: 'Other — not on this list' },
    ];
  }, [view]);

  // ── Loading (skeleton on the first load only) ──────────────────────────
  if (status === 'loading') {
    return (
      <section className="ferro-vax-req" aria-labelledby="vax-req-heading" data-testid="vax-req-loading">
        <h3 id="vax-req-heading" className="ferro-vax-req__heading">
          Required vaccinations
        </h3>
        <Skeleton variant="text" lines={3} />
      </section>
    );
  }

  // ── Older server (AC 8.2): a fact, not an error ────────────────────────
  if (status === 'unavailable') {
    return (
      <section className="ferro-vax-req" aria-labelledby="vax-req-heading" data-testid="vax-req-unavailable">
        <h3 id="vax-req-heading" className="ferro-vax-req__heading">
          Required vaccinations
        </h3>
        <p className="ferro-muted ferro-settings__footnote">Available after the next server update.</p>
      </section>
    );
  }

  // ── Error (what happened + how to recover) ─────────────────────────────
  if (status === 'error' || !view) {
    return (
      <section className="ferro-vax-req" aria-labelledby="vax-req-heading" data-testid="vax-req-error">
        <h3 id="vax-req-heading" className="ferro-vax-req__heading">
          Required vaccinations
        </h3>
        <Alert variant="error">
          We couldn&apos;t load your required vaccinations. Check your connection, then try again.
        </Alert>
        <div className="ferro-vax-req__actions">
          <Button variant="outline" onClick={() => void load()} data-testid="vax-req-retry">
            Try again
          </Button>
        </div>
      </section>
    );
  }

  const { requirements, limits } = view;
  const editingId = editor?.kind === 'edit' ? editor.vaccineId : null;
  const chosenCatalog =
    editor?.kind === 'add' && draft.choice !== '' && draft.choice !== OTHER_VACCINE
      ? (view.catalog.find((c) => c.key === draft.choice) ?? null)
      : null;
  const previewForName = preview && preview.name === otherName ? preview.data : null;
  const catalogMatch = previewForName?.catalogName
    ? (view.catalog.find((c) => c.name === previewForName.catalogName) ?? null)
    : null;

  const renderEditor = (mode: EditorMode, req: VaccinationRequirementView | null): JSX.Element => {
    // Service checkboxes: this shop's services, plus any service the rule
    // still names that is no longer offered, so it can be unticked.
    const serviceOptions = [...view.services];
    if (req?.appliesTo.kind === 'services') {
      for (const s of req.appliesTo.services) {
        if (!serviceOptions.some((o) => o.id === s.id)) serviceOptions.push(s);
      }
    }
    const coverage: { name: string; coverage: VaccineCoverage; note: string } | null =
      mode.kind === 'edit' && req
        ? { name: req.name, coverage: req.coverage, note: 'Counted as of today.' }
        : chosenCatalog
          ? { name: chosenCatalog.name, coverage: chosenCatalog.coverage, note: 'Counted as of today, with no grace period.' }
          : previewForName && !previewForName.catalogName && otherName.length > 0
            ? { name: otherName, coverage: previewForName.coverage, note: 'Counted as of today, with no grace period.' }
            : null;
    const blockedByCatalogMatch = mode.kind === 'add' && draft.choice === OTHER_VACCINE && catalogMatch !== null;
    const title = mode.kind === 'add' ? 'Add a required vaccine' : `Edit ${req?.name ?? 'vaccine'}`;

    return (
      <div
        className="ferro-vax-req__editor"
        role="group"
        aria-label={title}
        data-testid="vax-req-editor"
        onKeyDown={(e) => keepEnterLocal(e, () => void save())}
      >
        <p className="ferro-vax-req__editor-title">{title}</p>

        {mode.kind === 'add' ? (
          <div className="ferro-settings__field">
            <Select
              ref={(el) => {
                firstFieldRef.current = el;
              }}
              name="vaxReqVaccine"
              label="Vaccine"
              value={draft.choice}
              placeholder="Choose a vaccine"
              options={catalogOptions}
              onChange={(e) => {
                setDraft((d) => ({ ...d, choice: e.target.value }));
                setFormError(null);
              }}
              helperText={
                chosenCatalog?.hint ??
                (draft.choice === OTHER_VACCINE
                  ? 'For a vaccine that is not on the list.'
                  : 'Every spelling of a listed vaccine counts: DHPP covers DAPP, DA2PP and DHLPP.')
              }
              data-testid="vax-req-vaccine"
            />
          </div>
        ) : null}

        {mode.kind === 'add' && draft.choice === OTHER_VACCINE ? (
          <div className="ferro-settings__field">
            <Input
              name="vaxReqOtherName"
              label="Vaccine name"
              value={draft.otherName}
              maxLength={limits.maxNameLength}
              autoComplete="off"
              onChange={(e) => {
                setDraft((d) => ({ ...d, otherName: e.target.value }));
                setFormError(null);
              }}
              helperText="Only records with exactly this name count. Capitals, spaces and punctuation don't matter."
              data-testid="vax-req-other-name"
            />
            {catalogMatch ? (
              <div className="ferro-vax-req__guidance" data-testid="vax-req-other-guidance">
                {catalogMatch.alreadyRequired ? (
                  <p>
                    That&apos;s {catalogMatch.name}, which is already required. Edit it in the list instead.
                  </p>
                ) : (
                  <>
                    <p>
                      That&apos;s {catalogMatch.name} — choose it from the list so every spelling counts.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setDraft((d) => ({ ...d, choice: catalogMatch.key, otherName: '' }))}
                      data-testid="vax-req-use-catalog"
                    >
                      Choose {catalogMatch.name}
                    </Button>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        <fieldset className="ferro-settings__fieldset">
          <legend className="ferro-settings__label">Applies to</legend>
          <label className="ferro-settings__field ferro-settings__radio">
            <input
              type="radio"
              name="vaxReqAppliesTo"
              value="all"
              checked={draft.appliesKind === 'all'}
              onChange={() => setDraft((d) => ({ ...d, appliesKind: 'all' }))}
              data-testid="vax-req-applies-all"
              ref={
                mode.kind === 'edit'
                  ? (el) => {
                      if (draft.appliesKind === 'all') firstFieldRef.current = el;
                    }
                  : undefined
              }
            />{' '}
            <span>
              <strong>All services</strong>
            </span>
          </label>
          <label className="ferro-settings__field ferro-settings__radio">
            <input
              type="radio"
              name="vaxReqAppliesTo"
              value="services"
              checked={draft.appliesKind === 'services'}
              disabled={serviceOptions.length === 0}
              onChange={() => setDraft((d) => ({ ...d, appliesKind: 'services' }))}
              data-testid="vax-req-applies-some"
              ref={
                mode.kind === 'edit'
                  ? (el) => {
                      if (draft.appliesKind === 'services') firstFieldRef.current = el;
                    }
                  : undefined
              }
            />{' '}
            <span>
              <strong>Only some services</strong>
              {serviceOptions.length === 0 ? ' — your shop has no services set up yet.' : null}
            </span>
          </label>
          {draft.appliesKind === 'services' ? (
            <div className="ferro-vax-req__services" data-testid="vax-req-services">
              {serviceOptions.map((s) => (
                <Checkbox
                  key={s.id}
                  name={`vaxReqService-${s.id}`}
                  label={s.name}
                  checked={draft.serviceIds.includes(s.id)}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      serviceIds: e.target.checked
                        ? [...new Set([...d.serviceIds, s.id])]
                        : d.serviceIds.filter((id) => id !== s.id),
                    }))
                  }
                  data-testid={`vax-req-service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                />
              ))}
            </div>
          ) : null}
        </fieldset>

        <div className="ferro-settings__field">
          <Input
            name="vaxReqGrace"
            label="Grace period (days)"
            type="number"
            inputMode="numeric"
            min={0}
            max={limits.maxGracePeriodDays}
            step={1}
            value={draft.grace}
            onChange={(e) => setDraft((d) => ({ ...d, grace: e.target.value }))}
            helperText={`How many days after a shot expires a dog can still book online. 0 means none; up to ${limits.maxGracePeriodDays}.`}
            data-testid="vax-req-grace"
          />
        </div>

        <div className="ferro-settings__field">
          <Input
            name="vaxReqNote"
            label="Note to customers (optional)"
            value={draft.note}
            maxLength={limits.maxNoteLength}
            autoComplete="off"
            onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            helperText="Customers see this with the vaccine, for example “Every six months for boarders.”"
            data-testid="vax-req-note"
          />
        </div>

        {coverage ? (
          <div className="ferro-settings__field" data-testid="vax-req-preview">
            <p className="ferro-settings__preview">{coverageSentence(coverage.name, coverage.coverage)}</p>
            <p className="ferro-muted ferro-settings__footnote">{coverage.note}</p>
          </div>
        ) : null}

        {formError ? (
          <div data-testid="vax-req-form-error">
            <Alert variant="error">{formError}</Alert>
          </div>
        ) : null}

        <div className="ferro-vax-req__actions">
          <Button
            variant="primary"
            isLoading={saving}
            disabled={saving || blockedByCatalogMatch}
            onClick={() => void save()}
            data-testid="vax-req-save"
          >
            {mode.kind === 'add' ? 'Add requirement' : 'Save changes'}
          </Button>
          <Button variant="ghost" disabled={saving} onClick={closeEditor} data-testid="vax-req-cancel">
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <section className="ferro-vax-req" aria-labelledby="vax-req-heading" data-testid="vax-req-ready">
      <h3 id="vax-req-heading" className="ferro-vax-req__heading">
        Required vaccinations
      </h3>

      {notice ? (
        <p className="ferro-vax-req__notice" role="status" data-testid="vax-req-notice">
          {notice}
        </p>
      ) : null}

      {requirements.length === 0 ? (
        <div className="ferro-vax-req__empty" data-testid="vax-req-empty">
          <p className="ferro-settings__teaching">
            {canEdit
              ? 'No vaccines are required yet. Customers can book online without records until you add one.'
              : 'No vaccines are required yet. Customers can book online without records.'}
          </p>
          {canEdit && editor === null ? (
            <div className="ferro-vax-req__actions">
              <Button variant="primary" onClick={openAdd} data-testid="vax-req-add">
                Add a required vaccine
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <ul className="ferro-vax-req__list" data-testid="vax-req-list">
          {requirements.map((req) => (
            <li key={req.vaccineId} className="ferro-vax-req__row" data-testid={`vax-req-row-${req.vaccineId}`}>
              <p className="ferro-vax-req__title" data-testid={`vax-req-summary-${req.vaccineId}`}>
                {requirementSummary(req)}
              </p>
              <p className="ferro-vax-req__coverage" data-testid={`vax-req-coverage-${req.vaccineId}`}>
                {coverageSentence(req.name, req.coverage)}
              </p>
              {req.description ? (
                <p className="ferro-vax-req__note" data-testid={`vax-req-note-${req.vaccineId}`}>
                  <span className="ferro-vax-req__note-label">Note to customers:</span> {req.description}
                </p>
              ) : null}
              <AcceptsLine req={req} />

              {canEdit && editingId !== req.vaccineId ? (
                confirmRemoveId === req.vaccineId ? (
                  <div className="ferro-vax-req__confirm" data-testid={`vax-req-confirm-${req.vaccineId}`}>
                    <p>Remove {req.name}? Customers will no longer be asked for it.</p>
                    {removeError?.id === req.vaccineId ? (
                      <Alert variant="error">{removeError.message}</Alert>
                    ) : null}
                    <div className="ferro-vax-req__actions">
                      <Button
                        variant="danger"
                        isLoading={removing}
                        onClick={() => void remove(req)}
                        data-testid={`vax-req-remove-confirm-${req.vaccineId}`}
                      >
                        Remove {req.name}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={removing}
                        onClick={() => {
                          setConfirmRemoveId(null);
                          setRemoveError(null);
                        }}
                        data-testid={`vax-req-remove-cancel-${req.vaccineId}`}
                      >
                        Keep it
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="ferro-vax-req__actions">
                    <Button
                      variant="outline"
                      onClick={() => openEdit(req)}
                      aria-label={`Edit ${req.name}`}
                      data-testid={`vax-req-edit-${req.vaccineId}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        closeEditor();
                        setNotice(null);
                        setRemoveError(null);
                        setConfirmRemoveId(req.vaccineId);
                      }}
                      aria-label={`Remove ${req.name}`}
                      data-testid={`vax-req-remove-${req.vaccineId}`}
                    >
                      Remove
                    </Button>
                  </div>
                )
              ) : null}

              {editingId === req.vaccineId ? renderEditor({ kind: 'edit', vaccineId: req.vaccineId }, req) : null}
            </li>
          ))}
        </ul>
      )}

      {editor?.kind === 'add' ? renderEditor(editor, null) : null}

      {canEdit && editor === null && requirements.length > 0 ? (
        <div className="ferro-vax-req__actions">
          <Button variant="outline" onClick={openAdd} data-testid="vax-req-add">
            Add a required vaccine
          </Button>
        </div>
      ) : null}

      {view.unrecognizedRecordNames.length > 0 ? (
        <details className="ferro-vax-req__unknown" data-testid="vax-req-unrecognized">
          <summary>Names on your records we don&apos;t recognize</summary>
          <p className="ferro-muted ferro-settings__footnote">
            These are on your dogs&apos; records but don&apos;t count toward any vaccine on the list.
            {canEdit ? ' To require one, add it as Other with that exact name.' : ''}
          </p>
          <ul className="ferro-vax-req__unknown-list">
            {view.unrecognizedRecordNames.map((n) => (
              <li key={n.name}>
                {n.name} <span className="ferro-muted">· {n.dogs} {n.dogs === 1 ? 'dog' : 'dogs'}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {view.lastChange ? (
        <p className="ferro-settings__last-change" data-testid="vax-req-last-change">
          Last changed by <strong>{view.lastChange.byName}</strong>
          {whenLabel(view.lastChange.at) ? ` · ${whenLabel(view.lastChange.at)}` : ''}
        </p>
      ) : null}
    </section>
  );
}
